import type { CardRecord, DeckEntry } from "@/lib/types";
import { baseLegendKey } from "@/lib/domains";
import type { SideGuide, SidePackage } from "@/lib/side-guides";
import type { SideboardPrepDocument } from "@/lib/sideboardPrep";
import {
  syncDeck,
  syncDeckDelete,
  syncImportedPrepDeck,
  syncImportedPrepDeckDelete,
  syncInventoryQuantity,
  syncInventoryReplace,
  syncPrep,
  syncPrepDelete,
  syncPrepPlan,
  syncPrepPlanDelete,
  syncSideGuide,
  syncSideGuideDelete,
} from "@/lib/cloud-sync";

export type SavedDeckEntry = {
  cardId: string;
  quantity: number;
};

export type SavedDeck = {
  id: string;
  name: string;
  legendId: string;
  legendName: string;
  legendBaseKey: string;
  championId: string | null;
  entries: SavedDeckEntry[];
  battlefieldIds: string[];
  sideboard: SavedDeckEntry[];
  totalCards: number;
  createdAt: string;
  updatedAt: string;
};

export type InventoryEntry = {
  cardId: string;
  quantity: number;
};

export type PrepSidePlan = {
  sideIn: SavedDeckEntry[];
  sideOut: SavedDeckEntry[];
};

export type SavedPrepPlan = {
  id: string;
  deckId: string;
  deckName: string;
  opponentLegendId: string;
  opponentLegendName: string;
  label?: string;
  first: PrepSidePlan;
  second: PrepSidePlan;
  createdAt: string;
  updatedAt: string;
};

type LegacySavedPrepPlan = Partial<SavedPrepPlan> & {
  id: string;
  deckId: string;
  deckName: string;
  opponentLegendId: string;
  opponentLegendName: string;
  playOrder?: "first" | "second";
  sideIn?: SavedDeckEntry[];
  sideOut?: SavedDeckEntry[];
  createdAt: string;
  updatedAt: string;
};

function cleanSidePlan(plan?: Partial<PrepSidePlan> | null): PrepSidePlan {
  return {
    sideIn: (plan?.sideIn ?? []).filter((entry) => entry.quantity > 0),
    sideOut: (plan?.sideOut ?? []).filter((entry) => entry.quantity > 0),
  };
}

function normalizePrepPlan(raw: LegacySavedPrepPlan): SavedPrepPlan {
  if (raw.first || raw.second) {
    return {
      id: raw.id,
      deckId: raw.deckId,
      deckName: raw.deckName,
      opponentLegendId: raw.opponentLegendId,
      opponentLegendName: raw.opponentLegendName,
      label: raw.label ?? "",
      first: cleanSidePlan(raw.first),
      second: cleanSidePlan(raw.second),
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };
  }

  const legacyPlan = cleanSidePlan({ sideIn: raw.sideIn, sideOut: raw.sideOut });
  const empty = cleanSidePlan(null);
  return {
    id: raw.id,
    deckId: raw.deckId,
    deckName: raw.deckName,
    opponentLegendId: raw.opponentLegendId,
    opponentLegendName: raw.opponentLegendName,
    label: raw.label ?? "",
    first: raw.playOrder === "second" ? empty : legacyPlan,
    second: raw.playOrder === "second" ? legacyPlan : empty,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
  };
}

function mergePrepPlans(preps: SavedPrepPlan[]): SavedPrepPlan[] {
  const byKey = new Map<string, SavedPrepPlan>();

  for (const prep of preps) {
    const key = `${prep.deckId}::${prep.opponentLegendId}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, prep);
      continue;
    }

    const merged: SavedPrepPlan = {
      ...existing,
      label: prep.label || existing.label,
      first:
        prep.first.sideIn.length || prep.first.sideOut.length ? prep.first : existing.first,
      second:
        prep.second.sideIn.length || prep.second.sideOut.length ? prep.second : existing.second,
      createdAt: existing.createdAt < prep.createdAt ? existing.createdAt : prep.createdAt,
      updatedAt: existing.updatedAt > prep.updatedAt ? existing.updatedAt : prep.updatedAt,
    };
    byKey.set(key, merged);
  }

  return [...byKey.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export type ImportedPrepCardEntry = {
  cardCode: string;
  name: string;
  quantity: number;
  type?: string | null;
};

export type ImportedPrepDeck = {
  id: string;
  name: string;
  sourceCode: string;
  chosenChampion?: string | null;
  mainDeck: ImportedPrepCardEntry[];
  sideboard: ImportedPrepCardEntry[];
  createdAt: string;
  updatedAt: string;
};

const DECKS_KEY = "rifthub.decks.v1";
const INVENTORY_KEY = "rifthub.inventory.v1";
const PREPS_KEY = "rifthub.sideboard.v1";
const PREPA_KEY = "rifthub.prepa.v1";
const IMPORTED_PREPA_DECKS_KEY = "rifthub.prepa.imported-decks.v1";
const SIDE_GUIDES_KEY = "rifthub.side-guides.v1";

function nowIso() {
  return new Date().toISOString();
}

function makeId() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, JSON.stringify(value));
}

// ── Decks ────────────────────────────────────────────────────────────────────

export function listDecks(): SavedDeck[] {
  return readJson<SavedDeck[]>(DECKS_KEY, [])
    .map((d) => ({ ...d, sideboard: d.sideboard ?? [], battlefieldIds: d.battlefieldIds ?? [] }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function getDeck(id: string): SavedDeck | null {
  return listDecks().find((d) => d.id === id) ?? null;
}

export function saveDeck(input: {
  id?: string;
  name: string;
  legend: CardRecord;
  championId: string | null;
  entries: DeckEntry[];
  battlefieldIds: string[];
  sideboard?: DeckEntry[];
  runeEntries?: SavedDeckEntry[];
}): SavedDeck {
  const decks = listDecks();
  const now = nowIso();
  const entries: SavedDeckEntry[] = input.entries
    .filter((e) => e.quantity > 0)
    .map((e) => ({ cardId: e.card.id, quantity: e.quantity }));

  for (const rune of input.runeEntries ?? []) {
    const existing = entries.find((e) => e.cardId === rune.cardId);
    if (existing) existing.quantity += rune.quantity;
    else entries.push({ ...rune });
  }

  const sideboard: SavedDeckEntry[] = (input.sideboard ?? [])
    .filter((e) => e.quantity > 0)
    .map((e) => ({ cardId: e.card.id, quantity: e.quantity }));

  const totalCards = entries.reduce((s, e) => s + e.quantity, 0);
  const existing = input.id ? decks.find((d) => d.id === input.id) : null;

  const deck: SavedDeck = {
    id: existing?.id ?? input.id ?? makeId(),
    name: input.name.trim() || `Deck ${input.legend.name}`,
    legendId: input.legend.id,
    legendName: input.legend.name,
    legendBaseKey: baseLegendKey(input.legend.name),
    championId: input.championId,
    entries,
    battlefieldIds: input.battlefieldIds,
    sideboard,
    totalCards,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  const next = existing
    ? decks.map((d) => (d.id === deck.id ? deck : d))
    : [deck, ...decks];
  writeJson(DECKS_KEY, next);
  void syncDeck(deck);
  return deck;
}

export function deleteDeck(id: string) {
  writeJson(
    DECKS_KEY,
    listDecks().filter((d) => d.id !== id),
  );
  void syncDeckDelete(id);
}

export function hydrateDeckEntries(deck: SavedDeck, cards: CardRecord[]): DeckEntry[] {
  const byId = new Map(cards.map((c) => [c.id, c]));
  return deck.entries.flatMap((e) => {
    const card = byId.get(e.cardId);
    if (!card || e.quantity <= 0) return [];
    return [{ card, quantity: e.quantity }];
  });
}

// ── Inventory ────────────────────────────────────────────────────────────────

export function listInventory(): InventoryEntry[] {
  return readJson<InventoryEntry[]>(INVENTORY_KEY, []);
}

export function setInventoryQuantity(cardId: string, quantity: number): InventoryEntry[] {
  const entries = listInventory().filter((e) => e.cardId !== cardId);
  if (quantity > 0) entries.push({ cardId, quantity: Math.floor(quantity) });
  writeJson(INVENTORY_KEY, entries);
  void syncInventoryQuantity(cardId, quantity);
  return entries;
}

/** Remplace tout l'inventaire (import replace). */
export function replaceInventory(entries: InventoryEntry[]): InventoryEntry[] {
  const cleaned = entries
    .filter((e) => e.quantity > 0)
    .map((e) => ({ cardId: e.cardId, quantity: Math.floor(e.quantity) }));
  writeJson(INVENTORY_KEY, cleaned);
  void syncInventoryReplace(cleaned);
  return cleaned;
}

/**
 * Fusionne dans l'inventaire existant.
 * mode "set" : la quantité importée devient le total pour ces cartes.
 * mode "add" : on ajoute à la quantité déjà possédée.
 */
export function mergeInventory(
  entries: InventoryEntry[],
  mode: "set" | "add" = "set",
): InventoryEntry[] {
  const map = new Map(listInventory().map((e) => [e.cardId, e.quantity]));
  for (const e of entries) {
    if (e.quantity <= 0) continue;
    const qty = Math.floor(e.quantity);
    if (mode === "add") {
      map.set(e.cardId, (map.get(e.cardId) ?? 0) + qty);
    } else {
      map.set(e.cardId, qty);
    }
  }
  const next = [...map.entries()]
    .filter(([, q]) => q > 0)
    .map(([cardId, quantity]) => ({ cardId, quantity }));
  writeJson(INVENTORY_KEY, next);
  void syncInventoryReplace(next);
  return next;
}

// ── Sideboard mindmaps ───────────────────────────────────────────────────────

export type StoredSideboardPrep = SideboardPrepDocument & {
  id: string;
  createdAt: string;
  updatedAt: string;
};

export function listSideboardPreps(): StoredSideboardPrep[] {
  return readJson<StoredSideboardPrep[]>(PREPS_KEY, []).sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );
}

export function getSideboardPrep(id: string): StoredSideboardPrep | null {
  return listSideboardPreps().find((p) => p.id === id) ?? null;
}

export function saveSideboardPrep(doc: SideboardPrepDocument & { id?: string }): StoredSideboardPrep {
  const preps = listSideboardPreps();
  const now = nowIso();
  const existing = doc.id ? preps.find((p) => p.id === doc.id) : null;
  const saved: StoredSideboardPrep = {
    ...doc,
    id: existing?.id ?? makeId(),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  const next = existing
    ? preps.map((p) => (p.id === saved.id ? saved : p))
    : [saved, ...preps];
  writeJson(PREPS_KEY, next);
  void syncPrep(saved);
  return saved;
}

export function deleteSideboardPrep(id: string) {
  writeJson(
    PREPS_KEY,
    listSideboardPreps().filter((p) => p.id !== id),
  );
  void syncPrepDelete(id);
}

// ── Matchup preps ─────────────────────────────────────────────────────────────

export function listPrepPlans(): SavedPrepPlan[] {
  const raw = readJson<LegacySavedPrepPlan[]>(PREPA_KEY, []);
  const normalized = mergePrepPlans(raw.map(normalizePrepPlan));
  return normalized;
}

export function getPrepPlan(id: string): SavedPrepPlan | null {
  return listPrepPlans().find((p) => p.id === id) ?? null;
}

export function savePrepPlan(input: {
  id?: string;
  deckId: string;
  deckName: string;
  opponentLegendId: string;
  opponentLegendName: string;
  label?: string;
  first: PrepSidePlan;
  second: PrepSidePlan;
}): SavedPrepPlan {
  const preps = listPrepPlans();
  const now = nowIso();
  const existing =
    (input.id ? preps.find((p) => p.id === input.id) : null) ??
    preps.find((p) => p.deckId === input.deckId && p.opponentLegendId === input.opponentLegendId) ??
    null;
  const saved: SavedPrepPlan = {
    id: existing?.id ?? makeId(),
    deckId: input.deckId,
    deckName: input.deckName,
    opponentLegendId: input.opponentLegendId,
    opponentLegendName: input.opponentLegendName,
    label: input.label?.trim() || "",
    first: cleanSidePlan(input.first),
    second: cleanSidePlan(input.second),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  const next = existing
    ? preps.map((p) => (p.id === saved.id ? saved : p))
    : [saved, ...preps];
  writeJson(PREPA_KEY, next);
  void syncPrepPlan(saved);
  return saved;
}

export function deletePrepPlan(id: string) {
  writeJson(
    PREPA_KEY,
    listPrepPlans().filter((p) => p.id !== id),
  );
  void syncPrepPlanDelete(id);
}

export function listImportedPrepDecks(): ImportedPrepDeck[] {
  return readJson<ImportedPrepDeck[]>(IMPORTED_PREPA_DECKS_KEY, []).sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );
}

export function saveImportedPrepDeck(input: {
  id?: string;
  name: string;
  sourceCode: string;
  chosenChampion?: string | null;
  mainDeck: ImportedPrepCardEntry[];
  sideboard: ImportedPrepCardEntry[];
}): ImportedPrepDeck {
  const decks = listImportedPrepDecks();
  const now = nowIso();
  const existing = input.id ? decks.find((d) => d.id === input.id) : null;
  const saved: ImportedPrepDeck = {
    id: existing?.id ?? makeId(),
    name: input.name.trim() || existing?.name || "Deck importe",
    sourceCode: input.sourceCode.trim(),
    chosenChampion: input.chosenChampion ?? null,
    mainDeck: input.mainDeck.filter((entry) => entry.quantity > 0),
    sideboard: input.sideboard.filter((entry) => entry.quantity > 0),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  const next = existing
    ? decks.map((deck) => (deck.id === saved.id ? saved : deck))
    : [saved, ...decks];
  writeJson(IMPORTED_PREPA_DECKS_KEY, next);
  void syncImportedPrepDeck(saved);
  return saved;
}

export function deleteImportedPrepDeck(id: string) {
  writeJson(
    IMPORTED_PREPA_DECKS_KEY,
    listImportedPrepDecks().filter((deck) => deck.id !== id),
  );
  void syncImportedPrepDeckDelete(id);
}

// ── Side guides (contre X → in/out + alternatives OU) ─────────────────────────

function cleanEntries(entries: SavedDeckEntry[] | undefined): SavedDeckEntry[] {
  return (entries ?? []).filter((e) => e.quantity > 0);
}

function cleanPackage(pkg: SidePackage): SidePackage {
  return {
    id: pkg.id || makeId(),
    label: pkg.label?.trim() || "Option",
    when: pkg.when?.trim() || "",
    sideIn: cleanEntries(pkg.sideIn),
    sideOut: cleanEntries(pkg.sideOut),
  };
}

export function listSideGuides(deckId?: string): SideGuide[] {
  const all = readJson<SideGuide[]>(SIDE_GUIDES_KEY, [])
    .map((g) => ({
      ...g,
      coreIn: cleanEntries(g.coreIn),
      coreOut: cleanEntries(g.coreOut),
      alternatives: (g.alternatives ?? []).map(cleanPackage),
      playOrder: g.playOrder ?? "both",
    }))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return deckId ? all.filter((g) => g.deckId === deckId) : all;
}

export function getSideGuide(id: string): SideGuide | null {
  return listSideGuides().find((g) => g.id === id) ?? null;
}

export function saveSideGuide(input: {
  id?: string;
  deckId: string;
  againstLegendId?: string | null;
  againstLegendName?: string;
  againstLabel?: string;
  playOrder?: SideGuide["playOrder"];
  notes?: string;
  coreOut?: SavedDeckEntry[];
  coreIn?: SavedDeckEntry[];
  alternatives?: SidePackage[];
}): SideGuide {
  const guides = listSideGuides();
  const now = nowIso();
  const existing = input.id ? guides.find((g) => g.id === input.id) : null;
  const saved: SideGuide = {
    id: existing?.id ?? makeId(),
    deckId: input.deckId,
    againstLegendId: input.againstLegendId ?? existing?.againstLegendId ?? null,
    againstLegendName: (input.againstLegendName ?? existing?.againstLegendName ?? "").trim(),
    againstLabel: (input.againstLabel ?? existing?.againstLabel ?? "").trim(),
    playOrder: input.playOrder ?? existing?.playOrder ?? "both",
    notes: (input.notes ?? existing?.notes ?? "").trim(),
    coreOut: cleanEntries(input.coreOut ?? existing?.coreOut),
    coreIn: cleanEntries(input.coreIn ?? existing?.coreIn),
    alternatives: (input.alternatives ?? existing?.alternatives ?? []).map(cleanPackage),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };
  const next = existing
    ? guides.map((g) => (g.id === saved.id ? saved : g))
    : [saved, ...guides];
  writeJson(SIDE_GUIDES_KEY, next);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("rifthub:storage-sync"));
  }
  void syncSideGuide(saved);
  return saved;
}

export function deleteSideGuide(id: string) {
  writeJson(
    SIDE_GUIDES_KEY,
    listSideGuides().filter((g) => g.id !== id),
  );
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("rifthub:storage-sync"));
  }
  void syncSideGuideDelete(id);
}
